import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/useAuth';
import { AuthCard } from '../auth/AuthCard';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { GatewayHealthService } from '../services/gateway/GatewayHealthService';

export function BootManager({ children }) {
    const { user, loading } = useAuth();
    const [status, setStatus] = useState({
        auth: false,
        backend: false,
        database: false,
        ai: false
    });
    const [bootComplete, setBootComplete] = useState(false);
    
    // 1. Auth Check
    useEffect(() => {
        if (!loading && user) {
            setStatus(s => ({ ...s, auth: true }));
        } else if (!loading && !user) {
            setStatus(s => ({ ...s, auth: false }));
        }
    }, [user, loading]);

    // 2. Backend & DB Check (Depends on Auth)
    useEffect(() => {
        if (!status.auth) return;
        
        let backendInterval;
        let dbInterval;

        const checkBackend = async () => {
            try {
                const res = await fetch(`${import.meta.env.VITE_GATEWAY_URL || 'http://localhost:8082'}/v1/health`);
                if (res.ok) {
                    setStatus(s => ({ ...s, backend: true }));
                    if (backendInterval) clearInterval(backendInterval);
                }
            } catch (e) {
                // Ignore and retry
            }
        };

        const checkDB = async () => {
            try {
                const res = await authenticatedFetch(`${import.meta.env.VITE_GATEWAY_URL || 'http://localhost:8082'}/api/user/usage`);
                if (res && res.ok) {
                    setStatus(s => ({ ...s, database: true }));
                    if (dbInterval) clearInterval(dbInterval);
                }
            } catch (e) {
                // Ignore and retry
            }
        };

        checkBackend();
        checkDB();

        backendInterval = setInterval(checkBackend, 1000);
        dbInterval = setInterval(checkDB, 1000);

        return () => {
            clearInterval(backendInterval);
            clearInterval(dbInterval);
        };
    }, [status.auth]);

    // 3. AI Check (Depends on Backend and DB)
    useEffect(() => {
        if (!status.backend || !status.database) return;
        
        GatewayHealthService.start();
        
        const onOnline = () => {
            setStatus(s => ({ ...s, ai: true }));
            GatewayHealthService.off("gateway_online", onOnline);
        };
        
        if (GatewayHealthService.getState().online) {
            setStatus(s => ({ ...s, ai: true }));
        } else {
            GatewayHealthService.on("gateway_online", onOnline);
        }

        return () => {
            GatewayHealthService.off("gateway_online", onOnline);
        }
    }, [status.backend, status.database]);

    // Finalize
    useEffect(() => {
        if (status.auth && status.backend && status.database && status.ai) {
            setTimeout(() => setBootComplete(true), 800);
        }
    }, [status]);

    if (loading) return null;
    if (!user) return <AuthCard />;

    if (!bootComplete) {
        return (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black text-white font-mono" style={{ backgroundColor: '#0a0a0a' }}>
                <div className="mb-12 text-3xl font-bold tracking-[0.3em] text-emerald-400 opacity-80 shadow-emerald-400/20 drop-shadow-lg">
                    WINGMAN
                </div>
                <div className="flex flex-col gap-5 w-72 text-sm">
                    <div className="flex justify-between items-center border-b border-white/10 pb-2">
                        <span className="text-slate-400">Authentication</span>
                        <span className={status.auth ? "text-emerald-400" : "text-slate-600 animate-pulse"}>
                            {status.auth ? 'OK' : 'WAITING'}
                        </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-white/10 pb-2">
                        <span className="text-slate-400">Backend Server</span>
                        <span className={status.backend ? "text-emerald-400" : "text-slate-600 animate-pulse"}>
                            {status.backend ? 'OK' : 'WAITING'}
                        </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-white/10 pb-2">
                        <span className="text-slate-400">Database Layer</span>
                        <span className={status.database ? "text-emerald-400" : "text-slate-600 animate-pulse"}>
                            {status.database ? 'OK' : 'WAITING'}
                        </span>
                    </div>
                    <div className="flex justify-between items-center pb-2">
                        <span className="text-slate-400">AI Context Engine</span>
                        <span className={status.ai ? "text-emerald-400" : "text-slate-600 animate-pulse"}>
                            {status.ai ? 'OK' : 'WAITING'}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return children;
}
